import React, { useEffect, useMemo, useState } from 'react';
import { getLocationAssignments, saveLocationAssignments } from '../api';
import { groupBySite } from '../utils/siteGroups';
import { usePower } from '../state/PowerContext';

// Assign Ameren bill groups (meter-read cycles 1-21) to locations, grouped by
// physical site like the curtailment picker. The power company per site is
// fixed (Tony's rules, enforced server-side) and shown as a label only.
// Setting a bill group on a site row applies to all its gauges; if multiple
// rows are checked, any bill-group change among them applies to the whole
// selection. Changes save immediately.

function fmtDay(iso) {
  if (!iso) return '?';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Read dates outside 2026 (groups 1-2 start in Dec 2025) get an explicit year
function fmtReadDate(iso) {
  const base = fmtDay(iso);
  return iso.startsWith('2026') ? base : `${base} '${iso.slice(2, 4)}`;
}

function periodText(p) {
  if (!p) return '—';
  if (!p.start) return `starts ${fmtDay(p.end)}`;
  return `${fmtDay(p.start)} → ${fmtDay(p.end)}${p.days_left != null ? ` · ${p.days_left}d left` : ''}`;
}

const MIXED = '__mixed__';

export default function BillingGroupsPanel() {
  const { refreshViews } = usePower();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [saveState, setSaveState] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  const load = () => getLocationAssignments().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const allGroups = useMemo(
    () => (data ? groupBySite(data.locations, l => l.location) : []),
    [data]);

  const shownGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups
      .map(([g, members]) => {
        if (g.toLowerCase().includes(q)) return [g, members];
        const m = members.filter(x => x.location.toLowerCase().includes(q));
        return m.length ? [g, m] : null;
      })
      .filter(Boolean);
  }, [allGroups, filter]);

  const save = async (assignments) => {
    setData(prev => ({
      ...prev,
      locations: prev.locations.map(l => {
        const a = assignments.find(x => x.location === l.location);
        if (!a) return l;
        return {
          ...l, bill_group: a.bill_group, assigned: true,
          current_period: a.bill_group ? prev.groups[String(a.bill_group)]?.current_period : null,
        };
      }),
    }));
    setSaveState('saving…');
    try {
      await saveLocationAssignments(assignments);
      refreshViews(); // keep the top billing-group dropdown in sync
      setSaveState('saved');
      setTimeout(() => setSaveState(''), 1500);
    } catch (e) {
      setSaveState('save failed — reloading');
      load();
      setTimeout(() => setSaveState(''), 3000);
    }
  };

  // Set a bill group for target locations. If any target is checked, the
  // change applies to the entire checked selection (Ameren rows only).
  const applyGroup = (targets, billGroup) => {
    let locs = targets;
    if (targets.some(t => checked.has(t)) && checked.size > 0) {
      locs = Array.from(new Set([...checked, ...targets]));
    }
    const rows = data.locations.filter(
      l => locs.includes(l.location) && l.company === 'Ameren');
    if (rows.length === 0) return;
    save(rows.map(l => ({ location: l.location, bill_group: billGroup })));
  };

  const toggleChecked = (locs) => setChecked(prev => {
    const next = new Set(prev);
    const allIn = locs.every(l => next.has(l));
    locs.forEach(l => allIn ? next.delete(l) : next.add(l));
    return next;
  });

  const toggleExpand = (g) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  if (!data) {
    return <div className="panel"><h2>Billing groups</h2><div className="loading">Loading…</div></div>;
  }

  const unassigned = data.locations.filter(l => l.company === 'Ameren' && !l.bill_group).length;
  const groupOptions = Object.keys(data.groups).sort((a, b) => a - b);

  const groupSelect = (value, onChange, { mixed = false, title } = {}) => (
    <select value={mixed ? MIXED : (value || '')} title={title}
      onChange={e => { if (e.target.value !== MIXED) onChange(e.target.value ? Number(e.target.value) : null); }}>
      {mixed && <option value={MIXED} disabled>mixed</option>}
      <option value="">—</option>
      {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
    </select>
  );

  return (
    <div className="panel">
      <div className="curtail-head">
        <h2 style={{ margin: 0 }}>Billing groups</h2>
        <button type="button" className="bg-info-btn" title="Ameren billing cycle reference — read dates for every bill group"
          onClick={() => setShowInfo(true)}>i</button>
        <span className="curtail-subnote">
          Ameren 2026 meter-read cycles · pay period = read date → read date · company per site is fixed
        </span>
        {unassigned > 0 && (
          <span className="badge warning">{unassigned} Ameren gauge{unassigned === 1 ? '' : 's'} without a group</span>
        )}
        {saveState && <span className="curtail-subnote">{saveState}</span>}
      </div>

      <div className="bg-toolbar">
        <input type="text" placeholder="Filter locations…" value={filter}
          onChange={e => setFilter(e.target.value)} />
        {checked.size > 0 && (
          <>
            <span className="curtail-subnote">{checked.size} selected:</span>
            <select defaultValue="" onChange={e => {
              if (e.target.value) applyGroup(Array.from(checked), Number(e.target.value));
              e.target.value = '';
            }}>
              <option value="" disabled>Set bill group…</option>
              {groupOptions.map(g => (
                <option key={g} value={g}>Group {g} ({periodText(data.groups[g].current_period)})</option>
              ))}
            </select>
            <button className="ghost" onClick={() => setChecked(new Set())}>Clear selection</button>
          </>
        )}
      </div>

      <div className="bg-table-wrap">
        <table className="bg-table">
          <thead>
            <tr>
              <th></th>
              <th>Site / location</th>
              <th>Company</th>
              <th>Bill group</th>
              <th>Current pay period</th>
            </tr>
          </thead>
          <tbody>
            {shownGroups.map(([g, members]) => {
              const locs = members.map(m => m.location);
              const company = members[0].company; // uniform per site by construction
              const isAmeren = company === 'Ameren';
              const groups = new Set(members.map(m => m.bill_group ?? null));
              const common = groups.size === 1 ? members[0].bill_group : undefined;
              const open = expanded.has(g) || filter.trim() !== '';
              const selCount = locs.filter(l => checked.has(l)).length;

              if (members.length === 1) {
                const l = members[0];
                return (
                  <tr key={g}>
                    <td>
                      <input type="checkbox" checked={checked.has(l.location)}
                        onChange={() => toggleChecked([l.location])} />
                    </td>
                    <td className="bg-loc">{l.location}
                      {!l.in_gauges && <span className="curtail-subnote"> (not in gauges)</span>}
                    </td>
                    <td><span className={`bg-company bg-co-${l.company.toLowerCase()}`}>{l.company}</span></td>
                    <td>{isAmeren
                      ? groupSelect(l.bill_group, v => applyGroup([l.location], v))
                      : <span className="curtail-subnote">n/a</span>}</td>
                    <td className="bg-period">{periodText(l.current_period)}</td>
                  </tr>
                );
              }

              return (
                <React.Fragment key={g}>
                  <tr className="bg-site-row">
                    <td>
                      <input type="checkbox" checked={selCount === locs.length}
                        ref={el => { if (el) el.indeterminate = selCount > 0 && selCount < locs.length; }}
                        onChange={() => toggleChecked(locs)} />
                    </td>
                    <td className="bg-loc">
                      <button type="button" className="loc-group-caret" onClick={() => toggleExpand(g)}
                        title={open ? 'Collapse' : 'Show individual gauges'}>{open ? '▾' : '▸'}</button>
                      {g} <span className="loc-count">{members.length}</span>
                    </td>
                    <td><span className={`bg-company bg-co-${company.toLowerCase()}`}>{company}</span></td>
                    <td>{isAmeren
                      ? groupSelect(common, v => applyGroup(locs, v),
                          { mixed: common === undefined, title: 'Sets all gauges at this site' })
                      : <span className="curtail-subnote">n/a</span>}</td>
                    <td className="bg-period">
                      {isAmeren && common
                        ? periodText(data.groups[String(common)]?.current_period)
                        : common === undefined ? 'mixed' : '—'}
                    </td>
                  </tr>
                  {open && members.map(l => (
                    <tr key={l.location} className="bg-member-row">
                      <td>
                        <input type="checkbox" checked={checked.has(l.location)}
                          onChange={() => toggleChecked([l.location])} />
                      </td>
                      <td className="bg-loc bg-member">{l.location}
                        {!l.in_gauges && <span className="curtail-subnote"> (not in gauges)</span>}
                      </td>
                      <td></td>
                      <td>{isAmeren
                        ? groupSelect(l.bill_group, v => applyGroup([l.location], v))
                        : <span className="curtail-subnote">n/a</span>}</td>
                      <td className="bg-period">{periodText(l.current_period)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal" style={{ width: 880 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0 }}>Ameren 2026 billing cycles</h2>
              <button className="ghost" style={{ marginLeft: 'auto' }} onClick={() => setShowInfo(false)}>✕</button>
            </div>
            <p className="curtail-subnote" style={{ margin: '6px 0 12px' }}>
              Bill groups 1–21 from Ameren Illinois' published 2026 meter-read schedule.
              A pay period runs from one scheduled read date to the next — the highlighted
              dates bound the period we're in right now. Match the cycle dates on the
              Ameren bill to a row here to find the group number.
            </p>
            <table className="bg-info-table">
              <thead>
                <tr><th>Group</th><th>Current pay period</th><th>2026 scheduled read dates</th></tr>
              </thead>
              <tbody>
                {groupOptions.map(g => {
                  const info = data.groups[g];
                  const cp = info.current_period;
                  return (
                    <tr key={g}>
                      <td className="bg-info-num">{g}</td>
                      <td className="bg-period" style={{ whiteSpace: 'nowrap' }}>{periodText(cp)}</td>
                      <td>
                        <div className="bg-info-dates">
                          {info.read_dates.map(d => (
                            <span key={d}
                              className={`bg-read-date ${d === cp?.start || d === cp?.end ? 'cur' : ''}`}>
                              {fmtReadDate(d)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
